import { createSelector } from "@reduxjs/toolkit";

import { selectMultiImages } from "../../../../components/ImageUploader/images.slice";
import { FormId } from "../../../../components/enums";
import { RootState } from "../../../../store";
import { UIState } from "../../../../store/HOC/genericUI.slice";

import { ComponentId as AdditionalComponentId } from "./AdditionalData/enums";
import { ComponentId as ImagesDescriptionComponentId } from "./ImagesDescriptionData/enums";
import {
  DamagesAdditionalFormInputs,
  DamagesImagesDescriptionFormInputs,
  FormDataInputs,
} from "./damages.slice";

export const selectDamagesImagesDescriptionFormData = (
  store: RootState
): DamagesImagesDescriptionFormInputs =>
  store.ocProperty.damages.data[FormId.DAMAGES_IMAGES_DESCRIPTION];

export const selectDamagesAdditionalFormData = (
  store: RootState
): DamagesAdditionalFormInputs =>
  store.ocProperty.damages.data[FormId.DAMAGES_ADDITIONAL];

export const selectArePreviousDamagePresent = createSelector(
  selectDamagesAdditionalFormData,
  (additionalData) =>
    additionalData[AdditionalComponentId.ARE_PREVIOUS_DAMAGE_PRESENT] === "true"
);

export const selectAreOtherDamagesPresent = createSelector(
  selectDamagesAdditionalFormData,
  (additionalData) =>
    additionalData[AdditionalComponentId.ARE_OTHER_DAMAGES_PRESENT] === "true"
);

const selectDamagesDescription = createSelector(
  selectDamagesImagesDescriptionFormData,
  (data) => ({
    name: "Opis uszkodzeń",
    value: data[ImagesDescriptionComponentId.DESCRIPTION],
  })
);

const selectDamagesImages = createSelector(
  (data: RootState) =>
    selectMultiImages(data, {
      section: ImagesDescriptionComponentId.DAMAGE_IMAGES,
    }),
  (data) => ({
    name: "Zdjęcia mienia",
    value: Object.values(data).map((image) => image.src as string),
  })
);

const selectPreviousDamage = createSelector(
  selectDamagesAdditionalFormData,
  (data) => ({
    name: "Czy mienie przed zdarzeniem posiadało jakieś nienaprawione uszkodzenia?",
    value:
      data[AdditionalComponentId.ARE_PREVIOUS_DAMAGE_PRESENT] === "true"
        ? "tak"
        : "nie",
  })
);

const selectPreviousDamageDescription = createSelector(
  selectDamagesAdditionalFormData,
  (data) => ({
    name: "Opis wcześniejszych uszkodzeń",
    value: data[AdditionalComponentId.PREVIOUS_DAMAGE_DESCRIPTION],
  })
);

const selectOtherDamage = createSelector(
  selectDamagesAdditionalFormData,
  (data) => ({
    name: "Czy powstały inne szkody rzeczowe poza uszkodzonym mieniem?",
    value:
      data[AdditionalComponentId.ARE_OTHER_DAMAGES_PRESENT] === "true"
        ? "tak"
        : "nie",
  })
);

const selectOtherDamageDescription = createSelector(
  selectDamagesAdditionalFormData,
  (data) => ({
    name: "Opis dodatkowych szkód",
    value: data[AdditionalComponentId.OTHER_DAMAGES_DESCRIPTION],
  })
);

const selectPropertyPlaceDescription = createSelector(
  selectDamagesAdditionalFormData,
  (data) => ({
    name: "Gdzie można obejrzeć uszkodzone mienie",
    value: data[AdditionalComponentId.PROPERTY_PLACE],
  })
);

const selectDamagesData = createSelector(
  selectDamagesDescription,
  selectDamagesImages,
  selectPreviousDamage,
  selectPreviousDamageDescription,
  selectOtherDamage,
  selectOtherDamageDescription,
  selectPropertyPlaceDescription,
  (
    damagesDescription,
    damagesImages,
    previousDamage,
    previousDamageDescription,
    otherDamage,
    otherDamageDescription,
    propertyPlaceDescription
  ) => [
    damagesDescription,
    damagesImages,
    previousDamage,
    previousDamageDescription,
    otherDamage,
    otherDamageDescription,
    propertyPlaceDescription,
  ]
);

export const selectSummaryData = createSelector(
  selectDamagesData,
  (damages) => ({ damages })
);

export const selectFormData = (store: RootState): FormDataInputs =>
  store.ocProperty.damages.data;

export const selectUIData = (store: RootState): UIState =>
  store.ocProperty.damages.ui;
